import type { Driver, Vehicle, AssignmentDriver } from 'api/quote-marketplace';

const determineLicenseClassFromDates = (
  licensing: Record<string, unknown>,
  province: number,
): string | null => {
  if (province === 1) {
    if (licensing.class_g_license_date) return 'G';
    if (licensing.class_g2_license_date) return 'G2';
    if (licensing.class_g1_license_date) return 'G1';
  }

  if (province === 2 || province === 7) {
    if (licensing.class_5_license_date) return 'Class 5';
    if (licensing.class_7_license_date) return 'Class 7';
  }

  if (province === 5) {
    if (licensing.class_5_license_date) return 'Class 5';
    if (licensing.class_7_level_2_license_date) return '7-2';
    if (licensing.class_7_level_1_license_date) return '7-1';
  }

  return null;
};

const LEARNER_CLASSES_BY_PROVINCE: Record<number, string[]> = {
  1: ['G1'],
  2: ['Class 7'],
  5: ['7-1'],
  7: ['Class 7'],
};

const isLearnerByLicenseClass = (
  licenseClass: string | null,
  province: number,
): boolean => {
  if (!licenseClass) return false;
  const learnerClasses = LEARNER_CLASSES_BY_PROVINCE[province];
  if (!learnerClasses) return false;
  return learnerClasses.includes(licenseClass);
};

export const isLearnerDriver = (
  driver: Driver | AssignmentDriver,
  quoteProvince: number,
): boolean => {
  if ('is_learner' in driver) {
    return driver.is_learner;
  }

  if ('licensing' in driver && driver.licensing) {
    const licensing = driver.licensing as Record<string, unknown>;
    const licenseClass = determineLicenseClassFromDates(
      licensing,
      quoteProvince,
    );
    return isLearnerByLicenseClass(licenseClass, quoteProvince);
  }

  return false;
};

export const hasLicenseData = (
  driver: Driver | AssignmentDriver,
  quoteProvince: number,
): boolean => {
  if ('is_learner' in driver) {
    return !!driver.license_class;
  }

  if ('licensing' in driver && driver.licensing) {
    const licensing = driver.licensing as Record<string, unknown>;
    return !!determineLicenseClassFromDates(licensing, quoteProvince);
  }

  return false;
};

export const getLearnerLabel = (province: number | null): string => {
  if (!province) return 'Learners';
  switch (province) {
    case 1:
      return 'Learners (G1)';
    case 2:
    case 7:
      return 'Learners (Class 7)';
    case 5:
      return 'Learners (7-1)';
    default:
      return 'Learners';
  }
};

export interface AssignmentCleanupResult {
  vehicles: Vehicle[];
  hasChanges: boolean;
}

export const removeInvalidDriversFromAssignments = (
  vehicles: Vehicle[],
  drivers: Driver[],
  quoteProvince: number,
): AssignmentCleanupResult => {
  const invalidDriverIds = new Set(
    drivers
      .filter(
        (d) =>
          d.excluded ||
          isLearnerDriver(d, quoteProvince) ||
          !hasLicenseData(d, quoteProvince),
      )
      .map((d) => d.id),
  );

  if (invalidDriverIds.size === 0) {
    return { vehicles, hasChanges: false };
  }

  let hasChanges = false;
  const updatedVehicles = vehicles.map((vehicle) => {
    const updates: Partial<Vehicle> = {};

    if (
      vehicle.principal_driver_id !== null &&
      invalidDriverIds.has(vehicle.principal_driver_id)
    ) {
      updates.principal_driver_id = null;
      hasChanges = true;
    }

    const secondaryDrivers = vehicle.secondary_drivers ?? [];
    const filteredSecondary = secondaryDrivers.filter(
      (id) => !invalidDriverIds.has(id),
    );
    if (filteredSecondary.length !== secondaryDrivers.length) {
      updates.secondary_drivers = filteredSecondary;
      hasChanges = true;
    }

    const occasionalDrivers = vehicle.occasional_drivers ?? [];
    const filteredOccasional = occasionalDrivers.filter(
      (id) => !invalidDriverIds.has(id),
    );
    if (filteredOccasional.length !== occasionalDrivers.length) {
      updates.occasional_drivers = filteredOccasional;
      hasChanges = true;
    }

    const learnerDrivers = vehicle.learner_drivers ?? [];
    const filteredLearners = learnerDrivers.filter(
      (id) => !invalidDriverIds.has(id),
    );
    if (filteredLearners.length !== learnerDrivers.length) {
      updates.learner_drivers = filteredLearners;
      hasChanges = true;
    }

    if (Object.keys(updates).length > 0) {
      return { ...vehicle, ...updates };
    }
    return vehicle;
  });

  return {
    vehicles: hasChanges ? updatedVehicles : vehicles,
    hasChanges,
  };
};
